import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { FiAlertCircle, FiType, FiX } from "react-icons/fi";
import {
  RenameFolderDocument,
  type RenameFolderMutation,
  type RenameFolderMutationVariables,
} from "../../../generated/graphql";

interface RenameFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  currentName: string;
}

export function RenameFolderModal({
  isOpen,
  onClose,
  folderId,
  currentName,
}: RenameFolderModalProps) {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const [renameFolder, { loading }] = useMutation<
    RenameFolderMutation,
    RenameFolderMutationVariables
  >(RenameFolderDocument, {
    onCompleted: () => {
      setError(null);
      onClose();
    },
    onError: (err) => {
      setError(err.message);
    },
    // Refetch all GetFolders queries to update the UI
    refetchQueries: ["GetFolders"],
  });

  // Update name when currentName changes
  useEffect(() => {
    setName(currentName);
  }, [currentName]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setName(currentName);
      setError(null);
    }
  }, [isOpen, currentName]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Folder name is required");
      return;
    }

    if (name.trim() === currentName) {
      setError("Please enter a different name");
      return;
    }

    await renameFolder({
      variables: {
        id: folderId,
        name: name.trim(),
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-folder-title"
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <FiType className="h-5 w-5 text-gray-600" />
            <h2 id="rename-folder-title" className="text-lg font-semibold text-gray-900">
              Rename Folder
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
            disabled={loading}
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label htmlFor="folder-name" className="mb-2 block text-sm font-medium text-gray-700">
              Folder Name
            </label>
            <input
              id="folder-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter folder name"
              disabled={loading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
              <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "Renaming..." : "Rename"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
